/**
 * 📧 Webmailer Accounts Component
 * Verwaltung von T-Online Webmailer Accounts
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Plus, 
  Mail, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  TestTube,
  Upload,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const WebmailerAccounts = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const queryClient = useQueryClient();

  // Load webmailer accounts
  const { data: accounts, isLoading } = useQuery('webmailerAccounts', async () => {
    const response = await axios.get('/api/webmailer');
    return response.data.data;
  });

  // Load stats
  const { data: stats } = useQuery('webmailerStats', async () => {
    const response = await axios.get('/api/webmailer/stats');
    return response.data.data;
  });

  // Add account mutation
  const addAccountMutation = useMutation(
    (accountData) => axios.post('/api/webmailer', accountData),
    {
      onSuccess: () => {
        toast.success('Webmailer Account erfolgreich hinzugefügt!');
        setShowAddForm(false);
        queryClient.invalidateQueries('webmailerAccounts');
        queryClient.invalidateQueries('webmailerStats');
      },
      onError: (error) => {
        toast.error('Fehler beim Hinzufügen: ' + (error.response?.data?.error || error.message));
      }
    }
  );

  // Delete account mutation
  const deleteAccountMutation = useMutation(
    (accountId) => axios.delete(`/api/webmailer/${accountId}`),
    {
      onSuccess: () => {
        toast.success('Account gelöscht!');
        queryClient.invalidateQueries('webmailerAccounts');
        queryClient.invalidateQueries('webmailerStats');
      },
      onError: () => {
        toast.error('Fehler beim Löschen');
      }
    }
  );

  // Verify account mutation
  const verifyAccountMutation = useMutation(
    (accountId) => axios.post(`/api/webmailer/${accountId}/verify`),
    {
      onSuccess: (response) => {
        if (response.data.success) {
          toast.success('Account verifiziert! ✅');
        } else {
          toast.error('Verifizierung fehlgeschlagen: ' + response.data.error);
        }
        queryClient.invalidateQueries('webmailerAccounts');
      },
      onError: (error) => {
        toast.error('Fehler: ' + error.message);
      }
    }
  );

  // Test send mutation
  const testSendMutation = useMutation(
    ({ accountId, recipient }) => axios.post('/api/webmailer/test-send', {
      account_id: accountId,
      recipient,
      subject: 'Test-Mail vom Webmailer',
      html_content: '<p>Dies ist eine Test-Mail vom T-Online Webmailer.</p>',
      sender_name: 'Test'
    }),
    {
      onSuccess: (response) => {
        if (response.data.success) {
          toast.success('Test-Mail gesendet! ✅');
        } else {
          toast.error('Senden fehlgeschlagen: ' + response.data.error);
        }
        queryClient.invalidateQueries('webmailerAccounts');
      },
      onError: (error) => {
        toast.error('Fehler: ' + error.message);
      }
    }
  );

  // Bulk import mutation
  const bulkImportMutation = useMutation(
    (data) => axios.post('/api/webmailer/bulk', data),
    {
      onSuccess: (response) => {
        const data = response.data.data;
        toast.success(`✅ ${data.created.length} Accounts importiert, ${data.failed.length} fehlgeschlagen`);
        setShowBulkImport(false);
        queryClient.invalidateQueries('webmailerAccounts');
        queryClient.invalidateQueries('webmailerStats');
      },
      onError: (error) => {
        toast.error('Import fehlgeschlagen: ' + error.message);
      }
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              <Mail className="h-6 w-6 mr-2 text-purple-600 dark:text-purple-400" />
              Webmailer Accounts
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowBulkImport(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2"
            >
              <Upload className="h-4 w-4" />
              <span>Bulk Import</span>
            </button>
            
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>{showAddForm ? 'Schließen' : 'Account hinzufügen'}</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gray-50 dark:bg-dark-surface p-3 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total || 0}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Gesamt</div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{stats.active || 0}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Aktiv</div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{stats.ok || 0}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">OK</div>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{stats.blocked || 0}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Blocked</div>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{stats.total_sent || 0}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Gesendet</div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <BulkImportModal
          onSubmit={(data) => bulkImportMutation.mutate(data)}
          loading={bulkImportMutation.isLoading}
          onClose={() => setShowBulkImport(false)}
        />
      )}

      {/* Add Form */}
      {showAddForm && (
        <AddAccountForm
          onSubmit={(data) => addAccountMutation.mutate(data)}
          loading={addAccountMutation.isLoading}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {/* Accounts List */}
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg overflow-hidden border dark:border-dark-copper-dark">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-copper-dark">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Webmailer Accounts ({accounts?.length || 0})
          </h2>
        </div>

        {accounts && accounts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-dark-surface">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Account
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Statistik
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Erstellt
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-200 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-dark-copper-dark">
                {accounts.map((account) => (
                  <tr key={account.id} className={`hover:bg-gray-50 dark:hover:bg-dark-surface ${account.is_active === 0 ? 'opacity-50 bg-red-50 dark:bg-red-900/10' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Mail className={`h-5 w-5 mr-3 ${account.is_active === 0 ? 'text-red-400' : 'text-purple-400'}`} />
                        <div>
                          <div className={`text-sm font-medium ${account.is_active === 0 ? 'text-red-600 dark:text-red-400 line-through' : 'text-gray-900 dark:text-gray-200'}`}>
                            {account.email}
                          </div>
                          {account.from_name && (
                            <div className="text-sm text-gray-500 dark:text-gray-300">
                              {account.from_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={account.status_label} isActive={account.is_active} />
                      {account.last_error_message && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs truncate">
                          {account.last_error_message}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-200">
                        ✅ {account.success_count || 0} / ❌ {account.error_count || 0}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Heute: {account.sent_today || 0} / {account.daily_limit || 500}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {new Date(account.created_at).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => verifyAccountMutation.mutate(account.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Verifizieren"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            const recipient = prompt('Test-Mail an:', 'test@example.com');
                            if (recipient) {
                              testSendMutation.mutate({ accountId: account.id, recipient });
                            }
                          }}
                          className="text-green-600 hover:text-green-900"
                          title="Test-Mail senden"
                        >
                          <TestTube className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteAccountMutation.mutate(account.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Löschen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Mail className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-200">Keine Webmailer Accounts</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
              Importieren Sie T-Online Accounts für parallelen Webmail-Versand.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowBulkImport(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
              >
                <Upload className="h-4 w-4 mr-2" />
                Bulk Import
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status, isActive }) => {
  if (isActive === 0) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
        <XCircle className="h-3 w-3 mr-1" />
        Inaktiv
      </span>
    );
  }

  if (status === 'BLOCKED') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Blocked
      </span>
    );
  }

  if (status === 'ERROR') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
        <XCircle className="h-3 w-3 mr-1" />
        Fehler
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
      <CheckCircle className="h-3 w-3 mr-1" />
      OK
    </span>
  );
};

// Bulk Import Modal
const BulkImportModal = ({ onSubmit, loading, onClose }) => {
  const [csvContent, setCsvContent] = useState('');
  const [fromName, setFromName] = useState('');
  const [verify, setVerify] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Parse CSV (Format: email:password oder email,password)
    const lines = csvContent.split('\n').filter(line => line.trim());
    const accounts = lines.map(line => {
      const parts = line.includes(':') ? line.split(':') : line.split(',');
      return `${parts[0]?.trim()}:${parts[1]?.trim()}`;
    }).filter(a => a.includes('@'));
    
    onSubmit({
      accounts,
      from_name: fromName || null,
      verify
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-dark-secondary rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          📦 Bulk Import - T-Online Accounts
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Accounts (Format: email:password, eine pro Zeile)
            </label>
            <textarea
              value={csvContent}
              onChange={(e) => setCsvContent(e.target.value)}
              className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
              placeholder="account1@t-online.de:password1&#10;account2@t-online.de:password2&#10;..."
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Standard-Absendername (optional)
            </label>
            <input
              type="text"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="z.B. comdirect"
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="verify"
              checked={verify}
              onChange={(e) => setVerify(e.target.checked)}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label htmlFor="verify" className="ml-2 text-sm text-gray-700 dark:text-gray-200">
              Accounts vor Import verifizieren (langsamer, aber sicherer)
            </label>
          </div>
          
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md font-medium disabled:opacity-50"
            >
              {loading ? 'Importiere...' : 'Importieren'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Account Form
const AddAccountForm = ({ onSubmit, loading, onCancel }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    from_name: '',
    daily_limit: 500
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
        ➕ Webmailer Account hinzufügen
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              E-Mail *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="account@t-online.de"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Passwort *
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Absendername (optional)
            </label>
            <input
              type="text"
              value={formData.from_name}
              onChange={(e) => setFormData({...formData, from_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="z.B. comdirect"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Tägliches Limit
            </label>
            <input
              type="number"
              value={formData.daily_limit}
              onChange={(e) => setFormData({...formData, daily_limit: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              min="1"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            {loading ? 'Hinzufügen...' : 'Account hinzufügen'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WebmailerAccounts;
